import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

/**
 * Negates the given expression
 *
 * @param expression Expression to negate
 * @example
 * {
 *   '{}': {
 *     'isActive': { '@a': 'is_active' },
 *     'isNotActive': { not: { '@': 'isActive' } }
 *   }
 * }
 * // ->
 * { isActive: false, isNotActive: true },
 * { isActive: true, isNotActive: false },
 * ...
 */
export interface Not<out T = any> {
  /** {@link Not} */
  readonly [Key.Not]: T extends boolean ? Expression<boolean> : never
}
export namespace Not {
  export const isNot: TypeChecker<Not> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<Not>({
      [Key.Not]: Expression.isExpression as TypeChecker<Not[Key.Not]>,
    }),
  ).usingAlias('Fn.Not')
  export const assertIsNot: TypeAsserter<Not> = TypeAsserter.Factory.from(isNot)

  /**
   * @see {@link Node}
   */
  export class Node<T extends boolean = boolean> extends fnNode.Node<
    T,
    Key.Not,
    expressionNode.Node
  > {
    public readonly type = common.value.types.Intrinsic.Boolean

    constructor(arg: Node<T>['arg']) {
      super(Key.Not, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<Not> = {
    createNode: (fn, context) => {
      const nodeToNegate = expressionNode.from(fn[Key.Not], context)
      return expressionNode.ForValue.isForValue(nodeToNegate)
        ? new expressionNode.ForValue(!nodeToNegate.value)
        : new Node(nodeToNegate)
    },
    expressionsInArgs: fn => [fn[Key.Not]],
    typeChecker: isNot,
  }
}
